import * as THREE from "three";
import { inked, toon } from "../toon";
import type { SkaterLook } from "./look";
import { facets } from "./geom";

// What every face kind shares (head space, face +z): the eyes' materials,
// one eye, and an ear. Each kind's face (`head-toy.ts`, `head-natural.ts`)
// builds the rest around them.

export interface EyeMaterials {
  white: THREE.Material;
  iris: THREE.Material;
  pupil: THREE.Material;
  brow: THREE.Material;
}

export function eyeMaterials(look: SkaterLook): EyeMaterials {
  return {
    white: toon("#ffffff", { roughness: 0.2 }),
    iris: toon(look.eyes ?? "#3b8fd9", { roughness: 0.3 }),
    pupil: new THREE.MeshBasicMaterial({ color: 0x1b1530 }),
    brow: toon(look.hair.color, { roughness: 0.7 }, "hair"),
  };
}

/** One eye on `side` (-1 or 1): a white, a colored iris and a pupil, scaled by `eyes` (1 is the cartoon's). */
export function buildEye(side: number, eyes: number, mats: EyeMaterials): THREE.Group {
  const eye = new THREE.Group();
  eye.name = "eye";
  eye.position.set(side * 0.075, 0.035, 0.16);
  if (eyes !== 1) eye.scale.setScalar(eyes);
  const white = new THREE.Mesh(new THREE.SphereGeometry(0.055, facets(20), facets(14, 2)), mats.white);
  white.scale.set(0.9, 1.2, 0.6);
  eye.add(white);
  const ir = new THREE.Mesh(new THREE.SphereGeometry(0.03, facets(16), facets(12, 2)), mats.iris);
  ir.scale.set(1, 1.15, 0.5);
  ir.position.set(side * 0.004, -0.004, 0.028);
  eye.add(ir);
  const p = new THREE.Mesh(new THREE.SphereGeometry(0.016, facets(12), facets(8, 2)), mats.pupil);
  p.scale.set(1, 1.15, 0.5);
  p.position.set(side * 0.004, -0.004, 0.037);
  eye.add(p);
  return eye;
}

/** An ear on `side` (-1 or 1). */
export function buildEar(side: number, skin: THREE.Material): THREE.Mesh {
  const ear = inked(new THREE.SphereGeometry(0.04, facets(12), facets(10, 2)), skin, 0.01);
  ear.scale.set(0.45, 1, 0.8);
  ear.position.set(side * 0.2, 0.0, -0.01);
  return ear;
}
